<script lang="ts">
    import { untrack } from 'svelte';
    import { fade } from 'svelte/transition';
    import type { Deal, Image, SocialProof } from '@shared/types/types';
    import SocialProofBadges from '@tile/components/social-proof-badges/SocialProofBadges.svelte';
    import DealBadge from './DealBadge.svelte';
    import { getImageUrl } from './ImageUrlBuilder.ts';
    import type { ImageAspectRatio, ImageLoadingOptions } from './types';

    interface Props {
        image: Image;
        imageLoadingOptions: ImageLoadingOptions;
        variationId: string;
        detailPageLink: string;
        socialProof?: SocialProof;
        onClick?: (event: Event) => void;
        deal?: Deal;
        size?: { width: string; height: string };
        aspectRatio?: ImageAspectRatio;
        rule?: string;
    }

    let {
        image,
        variationId,
        detailPageLink,
        onClick,
        socialProof,
        deal,
        imageLoadingOptions,
        size,
        aspectRatio,
        rule,
    }: Props = $props();

    let loadingError = $state(false);

    const webpSrc = $derived(getImageUrl(image.id, 'webp', aspectRatio));
    const jpegSrc = $derived(getImageUrl(image.id, 'jpeg', aspectRatio));
    const imgSrc = $derived(loadingError ? image.fallback : jpegSrc);

    /**
 *
 *
 */
    let lastVariationId = $state(untrack(() => variationId));
    $effect(() => {
        if (variationId !== lastVariationId) {
            loadingError = false;
            lastVariationId = variationId;
        }
    });

    const imagePaddingTop = $derived.by(() => {
        if (!aspectRatio) return '100%';

        switch (aspectRatio) {
            case 'landscape':
            case 'landscape-two-column':
                return '69.5%';
            case 'portrait':
                return '143%';
            default:
                return '100%';
        }
    });
</script>

<div
    class="reptile-product-image"
    data-qa={`ftfind-product-image-${variationId}`}
    style:width={size?.width}
    style:height={size?.height}
    style:padding-top={imagePaddingTop}
>
    {#if deal}
        <DealBadge {deal} {variationId} {rule} />
    {/if}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <oc-link-v2
        class="reptile-product-image__link"
        base64-href={btoa(detailPageLink)}
        onclick={onClick}
        data-link-type="image"
    >
        {#key variationId}
            <picture class="reptile-product-image__image" in:fade>
                {#if !loadingError}
                    <source srcset={webpSrc} type="image/webp" />
                    <source srcset={jpegSrc} type="image/jpeg" />
                {/if}
                <img
                    class="reptile-product-image__image"
                    src={imgSrc}
                    alt={image.alt}
                    onerror={() => {
                        loadingError = true;
                    }}
                    loading={imageLoadingOptions.loading}
                    fetchpriority={imageLoadingOptions.fetchpriority}
                />
            </picture>
        {/key}
        {#if socialProof && Object.values(socialProof).some(Boolean)}
            <div class="reptile-product-image__social-proof-badges">
                <SocialProofBadges {socialProof} />
            </div>
        {/if}
    </oc-link-v2>
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    .reptile-product-image {
        position: relative;

        &__link {
            position: absolute;
            display: block;
            inset: 0;
            width: 100%;
            background-color: tokens.$oc-base-color-white-100;
            padding: tokens.$oc-semantic-spacing-12 tokens.$oc-semantic-spacing-12 0;
            user-select: none;
        }

        &__image {
            display: block;
            height: 100%;
            width: 100%;
            object-fit: contain;
            object-position: center;
        }

        &__social-proof-badges {
            position: absolute;
            bottom: 0;
            left: 0;
            margin-left: 10px;
            margin-right: 10px;
            margin-bottom: tokens.$oc-semantic-spacing-25;
        }
    }
</style>
